import { formatDistanceToNowStrict, differenceInHours } from 'date-fns';
import getDateLocale from 'src/utils/getDateLocale';
import { useState } from "react";
import { useTranslation } from 'react-i18next';
import { RiInstagramLine } from "react-icons/ri";

// Shadcn UI Components
import { Button } from 'src/@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from "src/@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuPortal,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "src/@/components/ui/dropdown-menu";

// Utils & Components
import { formataNumeroCelular, verifyRules } from "src/utils/functions";
import TagsComponent from 'src/components/tags/TagsComponent';
import Iconify from 'src/components/Iconify';
import { cn } from 'src/@/lib/utils';

const AVATAR_COLORS = [
  'bg-violet-600', 'bg-blue-600', 'bg-emerald-600', 'bg-amber-600',
  'bg-rose-600', 'bg-cyan-600', 'bg-indigo-600', 'bg-teal-600',
];

function getAvatarColor(name) {
  if (!name) return AVATAR_COLORS[0];
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

function getInitials(name) {
  if (!name) return '?';
  const names = name.split(' ');
  let initials = names[0].substring(0, 1).toUpperCase();
  if (names.length > 1) initials += names[names.length - 1].substring(0, 1).toUpperCase();
  return initials;
}
import { ButtonLoading } from 'src/@/components/use/ButtonLoading';
import { ZuiTooltip } from 'src/@/components/use/tooltip';
import { useSnackbar } from 'notistack';
import v2_actionFinalizarChat from 'src/actions/v2/chats/v2_actionFinalizarChat';
import useAuth from 'src/hooks/useAuth';
import { removeChatFromList, setPushChatList, updateChatList } from 'src/redux/slices/chat';
import { useDispatch } from 'react-redux';

const TitleChat = ({ chat }) => {
  const formattedNumber = chat?.numeroCliente?.startsWith("55")
    ? formataNumeroCelular(chat.numeroCliente)
    : `+${chat.numeroCliente}`;

  const displayText = chat.nomeCliente
    ? chat.nomeCliente
    : formattedNumber;

  return <span className="text-[13px] font-semibold text-gray-900 dark:text-white truncate">{displayText}</span>;
};

const DropDownMenuOptions = ({
  chat,
  onFinishWithMsg,
  onFinishWithoutMsg,
  onMarkAsUnread,
  user,
}) => {
  const { t } = useTranslation('chat');
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button className="w-5 h-5 opacity-0 group-hover:opacity-100 transition-opacity" size="icon" variant="ghost">
          <Iconify width={12} icon="mdi:dots-horizontal" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent>
        <DropdownMenuSub>
          <DropdownMenuSubTrigger>
            <span>{t('header.finalizeChat')}</span>
          </DropdownMenuSubTrigger>
          <DropdownMenuPortal>
            <DropdownMenuSubContent>
              <DropdownMenuItem onClick={onFinishWithMsg}>
                <span>{t('finalize.buttonWithMessage')}</span>
              </DropdownMenuItem>
              {verifyRules(user.rules, "finalizar_sem_mensagem") && (
                <DropdownMenuItem onClick={onFinishWithoutMsg}>
                  <span>{t('finalize.buttonNoMessage')}</span>
                </DropdownMenuItem>
              )}
            </DropdownMenuSubContent>
          </DropdownMenuPortal>
        </DropdownMenuSub>

        {chat.novaMensagem !== 'sim' && (
          <DropdownMenuItem onClick={onMarkAsUnread}>
            <span>{t('header.markUnread')}</span>
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

export default function ChatAtendimentoItem({
  chat,
  onSelectConversation,
  actionMarcarNaoLido,
  isOpenSidebar,
  onFinishChat,
  key,
  disabled,
  onAceitarChat,
  searchItem,
  isSearch
}) {
  const [anchorEl, setAnchorEl] = useState(null);
  const statusChat = chat?.statusRoom;
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useAuth();
  const dispatch = useDispatch();
  const { t } = useTranslation('chat');

  function handleSelectChat() {
    if (statusChat === "Pendente") {
      if (user.level !== "root") {
        enqueueSnackbar(t('list.pendingAcceptHint'), { variant: "error" });
        return;
      }
    }
    onSelectConversation();
  }

  function validaCanal() {
    if (chat.canal === "instagram") {
      return <RiInstagramLine size={11} color="#ff1803" />
    } else if (["whatsapp", "whatsapp_business"].includes(chat.canal)) {
      return <Iconify icon="logos:whatsapp-icon" width={11} />
    } else {
      return <Iconify icon="logos:whatsapp-icon" width={11} />
    }
  }

  function formatLastMessage() {
    if (!chat?.lastMessageWpp?.message) return "";
    let lastMessage = chat?.lastMessageWpp?.message;

    if (chat?.lastMessageWpp?.fromMe) {
      lastMessage = lastMessage?.replace(/\*_[^_]+_\*/g, '');
      lastMessage = t('header.you') + ": " + lastMessage;
    }

    if (lastMessage.length > 30) {
      lastMessage = lastMessage?.substring(0, 30) + "...";
    }

    return lastMessage || "";
  }

  function getBorderClass() {
    if (chat?.canal !== "whatsapp_business") return "";
    if (!chat?.lastDateMsgClient) return "";

    const now = new Date();
    const lastMsgDate = new Date(chat?.lastDateMsgClient);
    const hoursElapsed = differenceInHours(now, lastMsgDate);
    const hoursRemaining = 24 - hoursElapsed;

    if (hoursRemaining <= 0) return "border-l-[3px] border-l-red-500";
    if (hoursRemaining <= 6) return "border-l-[3px] border-l-yellow-500";
    if (hoursRemaining <= 12) return "border-l-[3px] border-l-blue-300";
    return "";
  }

  const handleFinalizarChat = async (chatId, sendMessage, statusOrigin, chat) => {
    let data = {
      userId: user.id,
      sendMessage: sendMessage
    };
    try {
      dispatch(removeChatFromList({ chatId: chatId, status: statusOrigin }))
      v2_actionFinalizarChat(chatId, data);
      setPushChatList(chat, statusOrigin);

      setTimeout(() => {
        let dataToUpdade = {
          _id: chat._id,
          scope: {
            statusRoom: 'Finalizado',
            lastUpdate: new Date()
          }
        };
        dispatch(updateChatList(dataToUpdade));
      }, 200)

    } catch (error) {
      enqueueSnackbar(t('header.finalizeErrorRetry'), { variant: "error" });
      console.log("Erro ao finalizar chat");
    }
  };

  const isUnread = chat.novaMensagem === 'sim';
  const hasTags = chat?.tags_ids && chat.tags_ids.length > 0;

  return (
    <div
      className={`group relative px-3 py-2 border-b border-gray-100 dark:border-neutral-800 cursor-pointer transition-colors hover:bg-gray-50 dark:hover:bg-neutral-800/50 ${
        isUnread && statusChat === "Atendendo" ? "bg-violet-100/80 dark:bg-violet-500/15" : ""
      } ${getBorderClass()} ${disabled ? 'pointer-events-none opacity-50' : ''}`}
      key={key}
      onClick={handleSelectChat}
    >
      <div className="flex gap-2.5">
        {/* Avatar */}
        <div className="relative flex-shrink-0 mt-0.5">
          <Avatar className="w-9 h-9 rounded-full">
            <AvatarImage src={chat.fotoPerfil} alt={chat.nomeCliente} />
            <AvatarFallback className={cn(getAvatarColor(chat.nomeCliente || chat.numeroCliente), "rounded-full text-white text-[11px] font-bold")}>
              {getInitials(chat.nomeCliente || chat.numeroCliente)}
            </AvatarFallback>
          </Avatar>
          <div className="absolute -top-0.5 -right-0.5 w-4 h-4 rounded-full bg-white dark:bg-neutral-900 flex items-center justify-center shadow-sm">
            {validaCanal()}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          {/* Row 1: Name + Time */}
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-1.5 min-w-0 flex-1">
              {chat.canal === "instagram" ? (
                <span className={`text-[13px] truncate ${isUnread ? 'font-semibold' : 'font-medium'} text-gray-900 dark:text-white`}>
                  @{chat?.instagramUser?.profile?.username}
                </span>
              ) : (
                <TitleChat chat={chat} />
              )}
              {isUnread && <div className="w-2 h-2 rounded-full bg-violet-500 flex-shrink-0" />}
            </div>

            <div className="flex items-center gap-1.5 flex-shrink-0">
              {chat.dataUltimaMensagem && (
                <span className="text-[10px] text-gray-400 dark:text-neutral-500 whitespace-nowrap">
                  {formatDistanceToNowStrict(new Date(chat.dataUltimaMensagem), { addSuffix: false, locale: getDateLocale() })}
                </span>
              )}
            </div>
          </div>

          {/* Row 2: Last message + Channel badge */}
          <div className="flex items-center justify-between gap-2 mt-0.5">
            <span className="text-xs text-gray-500 dark:text-neutral-400 truncate flex-1">
              {chat?.statusRoom !== "Pendente" ? formatLastMessage() : chat.nome_canal}
            </span>

            <span
              className={`flex-shrink-0 inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-semibold uppercase leading-none ${
                chat?.canal === "whatsapp"
                  ? "bg-green-100 text-green-700 dark:bg-green-500/15 dark:text-green-400"
                  : chat?.canal === "whatsapp_business"
                    ? "bg-green-500 text-white"
                    : chat?.canal === "instagram"
                      ? "bg-pink-100 text-pink-700 dark:bg-pink-500/15 dark:text-pink-400"
                      : "bg-gray-100 text-gray-600 dark:bg-neutral-700 dark:text-neutral-300"
              }`}
            >
              <span className="truncate max-w-[70px]">{chat.nome_canal}</span>
            </span>
          </div>

          {/* Row 3: Tags + Agent info + Actions */}
          {(hasTags || isSearch || statusChat !== "Atendendo" || (statusChat === "Atendendo" && (user.level === "root" || chat.direciona_usuarios?.includes(user.id)))) && (
            <div className="flex items-center justify-between mt-1" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center gap-1 min-w-0 flex-1">
                {hasTags && (
                  <TagsComponent
                    chat={chat}
                    chatId={chat?._id}
                    tags={chat?.tags_ids || []}
                  />
                )}
                {chat?.usuario_atendendo && (
                  <ZuiTooltip content={chat?.usuario_atendendo}>
                    <span className="text-[10px] text-gray-400 dark:text-neutral-500 truncate max-w-[80px] flex items-center gap-0.5">
                      <Iconify icon="fluent:person-support-28-filled" width={12} className="flex-shrink-0" />
                      {chat.usuario_atendendo.split(' ')[0]}
                    </span>
                  </ZuiTooltip>
                )}
              </div>

              <div className="flex items-center gap-0.5 flex-shrink-0">
                {statusChat === "Atendendo" && (user.level === "root" || chat.direciona_usuarios?.includes(user.id)) && (
                  <DropDownMenuOptions
                    chat={chat}
                    user={user}
                    isSearch={isSearch}
                    onMarkAsUnread={actionMarcarNaoLido}
                    onFinishWithMsg={() => handleFinalizarChat(chat._id, true, "Atendendo", chat)}
                    onFinishWithoutMsg={() => handleFinalizarChat(chat._id, false, "Atendendo", chat)}
                  />
                )}

                {statusChat === 'Finalizado' && (
                  <ZuiTooltip content={t('header.catchChat')}>
                    <Button size="icon" variant="ghost" className="w-6 h-6" onClick={onAceitarChat}>
                      <Iconify icon="fa-solid:hand-rock" width={12} />
                    </Button>
                  </ZuiTooltip>
                )}

                {statusChat === 'Geral' && (
                  <>
                    <ZuiTooltip content={t('header.finalizeChat')}>
                      <Button size="icon" variant="ghost" className="w-6 h-6" onClick={() => handleFinalizarChat(chat._id, false, "Geral", chat)}>
                        <Iconify icon="material-symbols:tab-close" width={14} />
                      </Button>
                    </ZuiTooltip>
                    <ZuiTooltip content={t('header.catchChat')}>
                      <Button size="icon" variant="ghost" className="w-6 h-6" onClick={onAceitarChat}>
                        <Iconify icon="fa-solid:hand-rock" width={12} />
                      </Button>
                    </ZuiTooltip>
                  </>
                )}

                {statusChat === 'Avaliando' && (
                  <>
                    <Iconify className="text-amber-500" icon="ph:star-duotone" width={14} />
                    <ZuiTooltip content={t('header.catchChat')}>
                      <Button size="icon" variant="ghost" className="w-6 h-6" onClick={onAceitarChat}>
                        <Iconify icon="fa-solid:hand-rock" width={12} />
                      </Button>
                    </ZuiTooltip>
                  </>
                )}

                {statusChat === "Pendente" && (
                  searchItem ? (
                    <ZuiTooltip content={t('header.catchChat')}>
                      <Button size="icon" variant="ghost" className="w-6 h-6" onClick={onAceitarChat}>
                        <Iconify icon="fa-solid:hand-rock" width={12} />
                      </Button>
                    </ZuiTooltip>
                  ) : (
                    <ButtonLoading loading={false} size="icon" variant="ghost" className="w-6 h-6" onClick={onAceitarChat}>
                      <Iconify icon="icon-park-solid:check-one" width={14} color="#00a884" />
                    </ButtonLoading>
                  )
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
